// deps
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// types
import { CroutonIds, ICroutonList, ShowCroutonArgs } from "@swiggy-private/cart-types";

// utils
import { isEmpty } from "@swiggy-private/js-utils";
import {
    LOCAL_STORAGE_KEYS,
    scrollElementIntoViewWithOffset,
    SESSION_STORAGE_KEYS,
    Storage,
} from "@swiggy-private/core-ui";
import {
    SessionVisibility,
    useLocalStorageBasedFrequencyCapping,
    AnalyticsContext,
} from "@swiggy-private/checkout-core";

// constants
import {
    CART_ELEMENT_IDS,
    SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
    SCROLL_MARGIN_TOP_WITHOUT_BANNER,
} from "@cart-client/components/cart-renderer/constants";

// helpers
import { getSavingsBannerData } from "@cart-client/helpers/Cart";

// components
import { AnimatedTitle, AnimatedTitlePreText } from "@cart-client/components/animated-title";
import { CartContext } from "@cart-client/contexts/CartContext";
import { useToingDeliveryModesAnalytics } from "@cart-client/hooks/useToingDeliveryModesAnalytics";

export interface UseDeliverySpeedCroutonProps {
    setCroutonVisibility: (visible: boolean) => void;
    onCloseCroutonImmediate: VoidFunction;
}

export interface UseDeliverySpeedCroutonReturnType {
    getDeliverySpeedCroutonRegistration: (crouton: ICroutonList, index: number) => ShowCroutonArgs | null;
    isDeliverySectionMounted: boolean;
}

const AUTO_DISMISS_MS = 4000;

const querySection = (): Element | null => {
    if (typeof document === "undefined") {
        return null;
    }
    return document.querySelector(CART_ELEMENT_IDS.DELIVERY_SECTION_CONTAINER_SELECTOR);
};

const useDeliverySpeedCrouton = ({
    setCroutonVisibility,
    onCloseCroutonImmediate,
}: UseDeliverySpeedCroutonProps): UseDeliverySpeedCroutonReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const { impressionToingDeliverySpeedCroutonShown, clickToingDeliverySpeedFtueCrouton } =
        useToingDeliveryModesAnalytics();

    const impressionEventRef = useRef(impressionEvent);
    impressionEventRef.current = impressionEvent;
    const impressionToingDeliverySpeedCroutonShownRef = useRef(impressionToingDeliverySpeedCroutonShown);
    impressionToingDeliverySpeedCroutonShownRef.current = impressionToingDeliverySpeedCroutonShown;

    const clickEventRef = useRef(clickEvent);
    clickEventRef.current = clickEvent;
    const clickToingDeliverySpeedFtueCroutonRef = useRef(clickToingDeliverySpeedFtueCrouton);
    clickToingDeliverySpeedFtueCroutonRef.current = clickToingDeliverySpeedFtueCrouton;

    const croutonMaxFreqCount = useMemo(
        () => cartResponse?.data?.toing_presentational_config?.delivery_modes_ftue_data?.crouton_max_count,
        [cartResponse],
    );

    const savingsBannerData = useMemo(() => getSavingsBannerData(cartResponse), [cartResponse]);

    const hasHandledRef = useRef<boolean>(
        Storage.getItem<boolean>(SESSION_STORAGE_KEYS.DELIVERY_SPEED_CROUTON_SEEN, false) ?? false,
    );

    const onCloseCroutonImmediateRef = useRef(onCloseCroutonImmediate);
    onCloseCroutonImmediateRef.current = onCloseCroutonImmediate;

    const { shouldProceed } = useLocalStorageBasedFrequencyCapping({
        key: LOCAL_STORAGE_KEYS.DELIVERY_SPEED_CROUTON_FREQUENCY_CAPPING_KEY,
        sessionVisibility: SessionVisibility.EVERYTIME_PER_SESSION,
        maxFrequency: croutonMaxFreqCount,
    });

    const [isDeliverySectionMounted, setIsDeliverySectionMounted] = useState<boolean>(() => !!querySection());

    useEffect(() => {
        if (isDeliverySectionMounted || typeof document === "undefined") {
            return undefined;
        }
        if (querySection()) {
            setIsDeliverySectionMounted(true);
            return undefined;
        }

        const observer = new MutationObserver(() => {
            if (querySection()) {
                setIsDeliverySectionMounted(true);
                observer.disconnect();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [isDeliverySectionMounted]);

    const handleClick = useCallback(() => {
        clickToingDeliverySpeedFtueCroutonRef.current(clickEventRef.current);
        scrollElementIntoViewWithOffset(
            CART_ELEMENT_IDS.DELIVERY_SECTION_CONTAINER_SELECTOR,
            isEmpty(savingsBannerData) ? SCROLL_MARGIN_TOP_WITHOUT_BANNER : SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
            true,
            CART_ELEMENT_IDS.CART_RENDERER_CONTAINER_SELECTOR,
        );
        onCloseCroutonImmediateRef.current();
    }, [savingsBannerData]);

    const handleClose = useCallback(() => {
        onCloseCroutonImmediateRef.current();
    }, []);

    const handleImpressionEvent = useCallback((text: string) => {
        if (!isEmpty(text)) {
            impressionToingDeliverySpeedCroutonShownRef.current(impressionEventRef.current, text);
        }
    }, []);

    const getDeliverySpeedCroutonRegistration = useCallback(
        (crouton: ICroutonList, index: number): ShowCroutonArgs | null => {
            if (hasHandledRef.current) {
                return null;
            }
            const { data } = crouton;
            if (isEmpty(data?.text)) {
                return null;
            }

            const sectionEl = querySection();
            if (!sectionEl) {
                return null;
            }

            if (!shouldProceed()) {
                return null;
            }

            hasHandledRef.current = true;

            return {
                id: CroutonIds.DELIVERY_SPEED_TOING,
                priority: index + 1,
                content: {
                    title: <AnimatedTitle text={data?.text || ""} />,
                    preTextEl: <AnimatedTitlePreText />,
                    durationOfCrouton: data?.timeout || AUTO_DISMISS_MS,
                    setCroutonVisibility,
                    onClick: handleClick,
                    onClose: handleClose,
                    impressionEvent: () => handleImpressionEvent(data?.text || ""),
                },
                forceRefreshContent: true,
            };
        },
        [handleClick, handleClose, handleImpressionEvent, setCroutonVisibility, shouldProceed],
    );

    return { getDeliverySpeedCroutonRegistration, isDeliverySectionMounted };
};

export default useDeliverySpeedCrouton;
